import { HGInterval, HGIntervalType, HGNow } from "./types";
import { intervalsAreEqual, shiftN } from "./intervals";

export interface QuickIntervalTarget {
  interval: HGInterval;
  endInterval: HGInterval | null;
}

export interface QuickIntervalPreset {
  id: string;
  label: string;
  title?: string;
  /**
   * A command rather than a position: "Now" would otherwise light up
   * together with "Today"/"This month"/… whenever the stack happens to sit
   * on the present, so it never reports itself as active.
   */
  transient?: boolean;
  /** null when the present moment isn't resolvable (e.g. an unknown it). */
  resolve(now: HGNow, currentIt: HGIntervalType): QuickIntervalTarget | null;
}

// HGNow is an index signature, so a missing key types as `bigint` even though
// it can be `undefined` at runtime (e.g. before the clock/engine is ready).
const at = (now: HGNow, it: HGIntervalType): bigint | undefined => now[it];

const single = (n: bigint, it: HGIntervalType): QuickIntervalTarget => ({
  interval: { n, it },
  endInterval: null,
});

const range = (from: bigint, to: bigint, it: HGIntervalType): QuickIntervalTarget => ({
  interval: { n: from, it },
  endInterval: { n: to, it },
});

export const QUICK_INTERVALS: QuickIntervalPreset[] = [
  {
    id: "now",
    label: "Now",
    title: "Jump to the present",
    transient: true,
    resolve: (now, currentIt) => {
      const n = at(now, currentIt);
      return n === undefined ? null : single(n, currentIt);
    },
  },
  {
    id: "today",
    label: "Today",
    resolve: (now) => {
      const d = at(now, "d");
      return d === undefined ? null : single(d, "d");
    },
  },
  {
    id: "yesterday",
    label: "Yesterday",
    resolve: (now) => {
      const d = at(now, "d");
      return d === undefined ? null : single(shiftN(d, -1n), "d");
    },
  },
  {
    id: "tomorrow",
    label: "Tomorrow",
    resolve: (now) => {
      const d = at(now, "d");
      return d === undefined ? null : single(shiftN(d, 1n), "d");
    },
  },
  {
    id: "thisWeek",
    label: "This week",
    resolve: (now) => {
      const w = at(now, "w");
      return w === undefined ? null : single(w, "w");
    },
  },
  {
    id: "lastWeek",
    label: "Last week",
    resolve: (now) => {
      const w = at(now, "w");
      return w === undefined ? null : single(shiftN(w, -1n), "w");
    },
  },
  {
    id: "thisMonth",
    label: "This month",
    resolve: (now) => {
      const m = at(now, "m");
      return m === undefined ? null : single(m, "m");
    },
  },
  {
    id: "lastMonth",
    label: "Last month",
    resolve: (now) => {
      const m = at(now, "m");
      return m === undefined ? null : single(shiftN(m, -1n), "m");
    },
  },
  {
    id: "last7d",
    label: "Last 7 days",
    title: "The 7 days ending today",
    resolve: (now) => {
      const d = at(now, "d");
      return d === undefined ? null : range(shiftN(d, -6n), d, "d");
    },
  },
  {
    id: "last30d",
    label: "Last 30 days",
    title: "The 30 days ending today",
    resolve: (now) => {
      const d = at(now, "d");
      return d === undefined ? null : range(shiftN(d, -29n), d, "d");
    },
  },
  {
    id: "last12m",
    label: "Last 12 months",
    title: "The 12 months ending this month",
    resolve: (now) => {
      const m = at(now, "m");
      return m === undefined ? null : range(shiftN(m, -11n), m, "m");
    },
  },
];

export function quickIntervalIsActive(
  preset: QuickIntervalPreset,
  now: HGNow,
  interval: HGInterval,
  endInterval: HGInterval | null,
): boolean {
  if (preset.transient) return false;
  const target = preset.resolve(now, interval.it);
  if (!target || !intervalsAreEqual(target.interval, interval)) return false;
  return target.endInterval === null
    ? endInterval === null
    : intervalsAreEqual(target.endInterval, endInterval);
}
